import { Component, effect, inject, signal, OnInit, ElementRef, computed } from '@angular/core';
import { ChatService } from '../../../services/chat/chat.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MediaService } from '../../../services/media/media.service';
import { HttpEventType } from '@angular/common/http';
import { ToasterService } from '../../../services/toaster/toaster.service';
import { IdentityService } from '../../../services/identity/identity.service';

@Component({
  selector: 'app-edit-room-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './edit-room-modal.html',
  styleUrl: './edit-room-modal.scss',
})
export class EditRoomModal implements OnInit {
  private client = inject(ChatService);
  private media = inject(MediaService);
  private identityService = inject(IdentityService);
  private el = inject(ElementRef);
  private toaster = inject(ToasterService);

  public arenaIcons = this.media.arenaIcons;

  public arena = this.client.arena;

  public onModalOpen() {
    const val = this.client.arena();
    if (!val) return;

    this.arenaSignal.set({ ...val });
    this.selectedIcon.set(null);

    const arenaId = val.parentGuid ?? val.guid;
    this.media.getArenaMedia(arenaId, 2);

    if (this.arenaForm) {
      this.arenaForm.reset({
        guid: val.guid,
        parentGuid: val.parentGuid,
        title: val.title,
        motd: val.motd,
        description: val.description,
        avatarURL: val.avatarURL,
        url: val.url,
        ownerId: val.ownerId,
        parentOwnerId: val.parentOwnerId,
        isProtected: val.isProtected,
        isOfficial: val.isOfficial,
        isPublic: val.isPublic,
        isStatic: val.isStatic,
        hasVoice: val.hasVoice,
        hasVideo: val.hasVideo,
        hasImages: val.hasImages,
        hasWebView: val.hasWebView,
        creationDate: val.creationDate,
        password: val.password,
        selectedGame: val.selectedGame,
        games: val.games,
      });
    } else {
      this.buildForm(val);
    }
  }

  public arenaSignal = signal<any>(null);
  public arenaForm!: FormGroup;

  public selectedIcon = signal<any>(null);

  public isDragging = signal(false);
  public isUploading = signal(false);
  public uploadProgress = signal(0);

  constructor() {
    // When icons are loaded, auto-select whichever one matches the arena's current avatarURL.
    effect(() => {
      const icons = this.arenaIcons();
      if (!icons || icons.length === 0) return;

      // Only auto-select if the user hasn't manually chosen a different icon yet
      // (i.e., selectedIcon is still null on initial open).
      if (this.selectedIcon() !== null) return;

      const currentUrl = this.arena()?.avatarURL ?? '';
      const matched = icons.find((icon: any) =>
        currentUrl.includes(icon.guid)
      );
      if (matched) {
        this.selectIcon(matched);
      }
    });
  }

  private buildForm(val: any) {
    this.arenaForm = new FormGroup({
      guid: new FormControl(val.guid),
      parentGuid: new FormControl(val.parentGuid),
      title: new FormControl(val.title, {
        validators: [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(50)
        ]
      }),
      motd: new FormControl(val.motd, {
        validators: [
          Validators.minLength(0),
          Validators.maxLength(100)
        ]
      }),
      description: new FormControl(val.description, {
        validators: [
          Validators.minLength(0),
          Validators.maxLength(200)
        ]
      }),
      avatarURL: new FormControl(val.avatarURL, {
        validators: [
          Validators.required,
          Validators.minLength(20)
        ]
      }),
      url: new FormControl(val.url),
      ownerId: new FormControl(val.ownerId),
      parentOwnerId: new FormControl(val.parentOwnerId),
      isProtected: new FormControl(val.isProtected),
      isOfficial: new FormControl(val.isOfficial),
      isPublic: new FormControl(val.isPublic),
      isStatic: new FormControl(val.isStatic),
      hasVoice: new FormControl(val.hasVoice),
      hasVideo: new FormControl(val.hasVideo),
      hasImages: new FormControl(val.hasImages),
      hasWebView: new FormControl(val.hasWebView),
      creationDate: new FormControl(val.creationDate),
      password: new FormControl(val.password ?? ""),
      selectedGame: new FormControl(val.selectedGame),
      games: new FormControl(val.games),
    });

    this.arenaForm.valueChanges.subscribe(formVal => {
      const arenaVal: any = {
        ...formVal,
        isProtected: formVal.password !== ''
      };
      this.arenaSignal.set(arenaVal);
    });
  }

  ngOnInit(): void {
    this.onModalOpen();

    const modalEl = this.el.nativeElement.querySelector('#editRoomModal');
    if (modalEl) {
      modalEl.addEventListener('show.bs.modal', () => {
        this.onModalOpen();
      });

      modalEl.addEventListener('hidden.bs.modal', () => {
        // Reset tab buttons
        const tabLinks = this.el.nativeElement.querySelectorAll('#editRoomTabs .nav-link');
        tabLinks.forEach((link: HTMLElement) => {
          if (link.id === 'details-tab') {
            link.classList.add('active');
            link.setAttribute('aria-selected', 'true');
          } else {
            link.classList.remove('active');
            link.setAttribute('aria-selected', 'false');
          }
        });

        // Reset tab panes
        const tabPanes = this.el.nativeElement.querySelectorAll('#editRoomTabsContent .tab-pane');
        tabPanes.forEach((pane: HTMLElement) => {
          if (pane.id === 'details') {
            pane.classList.add('show', 'active');
          } else {
            pane.classList.remove('show', 'active');
          }
        });
      });
    }
  }

  // --- Member Management ---
  public members = computed(() => {
    const list = this.client.members();
    if (!list) return [];
    return [...list].sort((a, b) => a.userName.localeCompare(b.userName, undefined, { sensitivity: 'base' }));
  });
  public newMemberUsername = signal('');
  public newMemberRole = signal('member');

  public addNewMember() {
    const username = this.newMemberUsername().trim();
    if (username === '') return;

    const newMember = {
      userId: '',
      userName: username,
      role: this.newMemberRole()
    };

    this.client.addMember(this.arena().guid, newMember);
    this.newMemberUsername.set('');
    this.newMemberRole.set('member');
  }

  public setMemberRole(member: any, role: string) {
    const updatedMember = { ...member, role: role };
    this.client.addMember(this.arena().guid, updatedMember);
  }

  public removeMember(userId: string) {
    this.client.removeMember(this.arena().guid, userId);
  }

  /*
   * Save updated room details back to the hub.
   */
  public update() {
    this.arenaForm.markAllAsTouched();
    if (this.arenaForm.valid) {
      const claims = this.identityService.getClaims();
      const currentUserId = claims?.nameid || claims?.sub || claims?.unique_name || claims?.name;
      const currentArena = this.client.arena() || {};

      const formVal = this.arenaForm.value;
      const arena = {
        ...currentArena,
        ...formVal,
        selectedGame: null,
        games: [],
        ownerId: (formVal.ownerId && formVal.ownerId.trim() !== '') ? formVal.ownerId : (currentArena.ownerId || currentUserId || null),
        parentOwnerId: (formVal.parentOwnerId && formVal.parentOwnerId.trim() !== '') ? formVal.parentOwnerId : (currentArena.parentOwnerId || null),
        isProtected: formVal.password !== ''
      };

      this.client.update(arena);

      const closeButton = this.el.nativeElement.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }
    } else {
      console.log(this.arenaForm);
    }
  }

  public selectIcon(icon: any) {
    let arena = this.arenaSignal();
    arena.avatarURL = `https://usercontent.h5arena.com/arenas/${icon.guid}${icon.fileExtension}`;
    this.arenaSignal.set(arena);
    this.arenaForm.controls['avatarURL'].setValue(arena.avatarURL);
    this.selectedIcon.set(icon);
  }

  // --- Image Upload Management ---
  public onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  public onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  public onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  public onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  private handleFile(file: File) {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    if (!validTypes.includes(file.type)) {
      console.error("Invalid file type.");
      return;
    }

    this.isUploading.set(true);
    this.uploadProgress.set(0);

    const arenaId = this.arena().parentGuid ?? this.arena().guid;

    this.media.getUploadUrl(arenaId, file.name, file.name).subscribe({
      next: (response) => {
        if (response && response.signedUrl) {
          this.media.uploadToS3(response.signedUrl, file).subscribe({
            next: (event) => {
              if (event.type === HttpEventType.UploadProgress && event.total) {
                this.uploadProgress.set(Math.round(100 * event.loaded / event.total));
              } else if (event.type === HttpEventType.Response) {
                this.isUploading.set(false);
                this.uploadProgress.set(100);
                this.media.getArenaMedia(arenaId, 2);
              }
            },
            error: (error) => {
              console.error("Error uploading file", error);
              this.isUploading.set(false);
              this.toaster.danger("Error uploading file.");
            }
          });
        }
      },
      error: (error) => {
        console.error("Error getting signed url", error);
        this.isUploading.set(false);
        this.toaster.danger("Failed to start file upload.");
      }
    });
  }

  public deleteImage(mediaId: string) {
    const arenaId = this.arena().parentGuid ?? this.arena().guid;
    this.media.deleteMedia(arenaId, mediaId).subscribe({
      next: () => {
        this.media.getArenaMedia(arenaId, 2);
      },
      error: (err) => {
        console.error("Error deleting media", err);
        this.toaster.danger("Failed to delete media.");
      }
    });
  }
}
